import React, { useEffect, useState, useMemo } from "react";
import {
  Badge,
  Button,
  Col,
  ProgressBar,
  Spinner,
  Table,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { BsPencilSquare, BsTrash } from "react-icons/bs";
import { format } from "date-fns";
import {
  deleteTaskById,
  fetchTasks,
} from "../../../../store/features/task/taskSlice";
import { IoDocumentTextOutline } from "react-icons/io5";
import TaskHistoryModal from "./TaskHistoryModal";
import { MdHistory } from "react-icons/md";
import AcceptedTaskFormModal from "./AcceptedTaskFormModal";
import { FcAcceptDatabase } from "react-icons/fc";
import ReactPaginate from "react-paginate";
import { fetchUserById } from "../../../../store/features/user/userSlice";
import { toast } from "react-toastify";
import { FaTasks } from "react-icons/fa";
import NoDataFound from "../../../../components/NoDataFound";
import { STATUS_OPTIONS } from "../../../../utils/filterOptions";
import { filterBySearchAndStatus } from "../../../../utils/filterUtils";
import FilterData from "../../../projectManagement/projects/components/FilterData";

const TaskTableData = ({
  setTaskInfo,
  setTabKey,
  wpInfo,
  setWpInfo,
  setShowTaskModal,
  deliverablesInfo,
  setDeliverablesInfo,
}) => {
  const dispatch = useDispatch();

  const { tasks, loading, pagination } = useSelector((state) => state.tasks);
  const { managers, staffs } = useSelector((state) => state.users);
  const { role, _id } = useSelector((state) => state.auth);

  const [showTaskHistory, setShowTaskHistory] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAccepted, setShowAccepted] = useState(false);
  const [taskInfoData, setTaskInfoData] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const limit = 10;

  useEffect(() => {
    dispatch(fetchTasks({ page: currentPage + 1, limit }));
  }, [dispatch, currentPage]);

  const toggleDescription = (id) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleEdit = (task) => {
    setTaskInfo(task);
    setShowTaskModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteTaskById(id)).unwrap();
      await dispatch(fetchTasks({ page: currentPage + 1, limit }));
      await dispatch(fetchUserById(_id));
      toast.info("Task deleted");
    } catch {
      toast.error("Error deleting task");
    }
  };

  const handleTaskHistory = (task) => {
    setSelectedTask({ taskId: task._id, title: task.title });
    setShowTaskHistory(true);
  };

  const handleAcceptedModal = (task) => {
    setTaskInfoData(task);
    setShowAccepted(true);
  };

  const getProgressStatus = (progress = 0) => {
    if (progress === 0) return "Pending";
    if (progress < 50) return "At Risk";
    if (progress < 100) return "On Track";
    return "Completed";
  };

  // const filteredTasks = (() => {
  //   if (role === "staff") {
  //     return tasks.filter((task) => !task.rejectionReason);
  //   }
  //   if (deliverablesInfo?._id) {
  //     return tasks.filter(
  //       (task) => task?.deliverable?._id === deliverablesInfo._id
  //     );
  //   }
  //   if (wpInfo?._id) {
  //     return tasks.filter((task) => task?.workPackage?._id === wpInfo._id);
  //   }
  //   return tasks;
  // })();

  const scopedTasks = useMemo(() => {
    if (role === "staff") {
      return tasks.filter((task) => !task.rejectionReason);
    }
    if (deliverablesInfo?._id) {
      return tasks.filter(
        (task) => task.deliverable?._id === deliverablesInfo._id
      );
    }
    if (wpInfo?._id) {
      return tasks.filter((task) => task.workPackage?._id === wpInfo._id);
    }
    return tasks;
  }, [tasks, role, deliverablesInfo, wpInfo]);
  const filteredTasks = useMemo(() => {
    return filterBySearchAndStatus({
      data: scopedTasks,
      searchTerm,
      statusFilter,
      searchKeys: ["title", "description"],
    });
  }, [scopedTasks, searchTerm, statusFilter]);
  return (
    <div>
      {loading ? (
        <Spinner animation="border" />
      ) : (
        <>
          {tasks.length > 0 ? (
            <div className="table-wrapper mt-3">
              <FilterData
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search tasks..."
                selectConfigs={[
                  {
                    label: "All Status",
                    value: statusFilter,
                    onChange: setStatusFilter,
                    options: STATUS_OPTIONS,
                  },
                ]}
              />
              <Col className="text-end me-2">
                <Button
                  size="sm"
                  style={{ fontSize: 10 }}
                  onClick={() => {
                    setWpInfo({});
                    setDeliverablesInfo({});
                  }}
                >
                  Show All Tasks
                </Button>
              </Col>

              <Table responsive>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Workpackage</th>
                    <th>Deliverable</th>
                    <th>Assigned To</th>
                    <th>Assigned By</th>
                    <th>Completion Notes</th>
                    <th>Accepted</th>
                    {role !== "staff" && <th>Decline Reason</th>}
                    <th>Progress Level</th>
                    <th>Progress Status</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Attachments</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map((task) => (
                    <tr key={task._id}>
                      <td style={{ minWidth: 150 }}>
                        {task.title}{" "}
                        {task.approvalStatus === "pending" && (
                          <Badge bg="danger" style={{ fontSize: 9 }}>
                            New
                          </Badge>
                        )}
                      </td>
                      <td style={{ minWidth: 200 }}>
                        {task.description?.length > 50 ? (
                          <>
                            {expandedDescriptions[task._id]
                              ? task.description
                              : `${task.description.slice(0, 50)}... `}
                            <span
                              onClick={() => toggleDescription(task._id)}
                              style={{ color: "blue", cursor: "pointer" }}
                            >
                              {expandedDescriptions[task._id]
                                ? "Show less"
                                : "Show more"}
                            </span>
                          </>
                        ) : (
                          task.description
                        )}
                      </td>
                      <td>{task.workPackage?.title}</td>
                      <td>{task.deliverable?.title}</td>
                      <td>
                        {staffs?.find((s) => s._id === task.assignedTo)?.name}
                      </td>
                      <td>
                        {managers?.find((m) => m._id === task.assignedBy)?.name}
                      </td>
                      <td>{task.completionNotes}</td>
                      <td>
                        {task.approvalStatus && (
                          <Badge
                            bg={
                              task.approvalStatus === "Accepted"
                                ? "success"
                                : "danger"
                            }
                          >
                            {task.approvalStatus}
                          </Badge>
                        )}
                      </td>
                      {role !== "staff" && <td>{task.rejectionReason}</td>}
                      <td className="text-center">
                        <div className="progessbar-wrapper">
                          <div className="progressbar-tooltip">{`${
                            task.progressLevel || 0
                          }%`}</div>
                          <ProgressBar now={task.progressLevel || 0} />
                        </div>
                      </td>
                      <td>
                        <Badge
                          className="mt-1"
                          bg={
                            (task.progressLevel || 0) < 50
                              ? "danger"
                              : (task.progressLevel || 0) < 100
                              ? "info"
                              : "success"
                          }
                        >
                          {getProgressStatus(task.progressLevel)}
                        </Badge>
                      </td>
                      <td>{format(task.startDate, "MM/dd/yyyy")}</td>
                      <td>{format(task.endDate, "MM/dd/yyyy")}</td>
                      <td style={{ minWidth: 120 }}>
                        {Array.isArray(task.attachments) &&
                          task.attachments.length > 0 && (
                            <div className="d-flex flex-wrap">
                              {task.attachments.map((doc, i) => (
                                <Button
                                  key={i}
                                  variant="light"
                                  className="me-1"
                                >
                                  <a
                                    href={doc}
                                    target="_blank"
                                    rel="noreferrer"
                                    download
                                  >
                                    <IoDocumentTextOutline size="1.2rem" />
                                  </a>
                                </Button>
                              ))}
                            </div>
                          )}
                      </td>
                      <td className="actions">
                        {role === "staff" && (
                          <Button
                            size="sm"
                            onClick={() => handleAcceptedModal(task)}
                            title="Accepted"
                          >
                            <FcAcceptDatabase />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleTaskHistory(task)}
                          title="History"
                        >
                          <MdHistory size={"1.2rem"} />
                        </Button>
                        {role !== "auditor" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleEdit(task)}
                              title="Edit"
                            >
                              <BsPencilSquare />
                            </Button>
                            {/* {role !== "auditor" && role ==="staff" && ( */}
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDelete(task._id)}
                              title="Delete"
                            >
                              <BsTrash />
                            </Button>
                            {/* )} */}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {pagination?.totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <ReactPaginate
                    breakLabel="..."
                    nextLabel="Next >"
                    onPageChange={(e) => setCurrentPage(e.selected)}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={1}
                    pageCount={pagination.totalPages}
                    previousLabel="< Prev"
                    forcePage={currentPage}
                    containerClassName="pagination"
                    activeClassName="active"
                  />
                </div>
              )}
            </div>
          ) : (
            <NoDataFound
              icon={<FaTasks size={50} />}
              title="Tasks not found"
              description="Try to add some tasks."
            />
          )}
        </>
      )}

      {showTaskHistory && selectedTask && (
        <TaskHistoryModal
          showTaskHistory={showTaskHistory}
          setShowTaskHistory={setShowTaskHistory}
          selectedTask={selectedTask}
        />
      )}

      {showAccepted && (
        <AcceptedTaskFormModal
          showAccepted={showAccepted}
          setShowAccepted={setShowAccepted}
          taskInfoData={taskInfoData}
        />
      )}
    </div>
  );
};

export default TaskTableData;
